import React from 'react';
import {
  X,
  ChefHat,
  Package,
  Edit2,
  TrendingUp,
  TrendingDown,
  FileText,
  Clock,
  Printer,
  Copy,
} from 'lucide-react';

/**
 * Recipe Viewer Component - Clean & Crisp Design
 *
 * onEdit      — undefined if user lacks EDIT permission      → button hidden
 * onDuplicate — undefined if user lacks DUPLICATE permission → button hidden
 */

export default function RecipeViewer({ recipe, onEdit, onClose, onDuplicate }) {
  if (!recipe) return null;

  const {
    menuItemName,
    categoryName,
    ingredients = [],
    totalCost: rawTotalCost,
    sellingPrice: rawSellingPrice,
    basePrice: rawBasePrice,
    prepNotes,
    updatedAt,
  } = recipe;

  // ---------- NUMERIC HANDLING & DERIVED VALUES ----------

  const computedTotalCost = ingredients.reduce((sum, ing) => {
    const qty = Number(ing.quantity ?? 0);
    const unitCost = Number(ing.unitCost ?? 0);
    const rowCost =
      typeof ing.totalCost === 'number'
        ? ing.totalCost
        : qty * unitCost;

    return sum + (Number.isNaN(rowCost) ? 0 : rowCost);
  }, 0);

  const safeTotalCost =
    typeof rawTotalCost === 'number' &&
    !Number.isNaN(rawTotalCost) &&
    rawTotalCost > 0
      ? rawTotalCost
      : computedTotalCost;

  const safeSellingPrice =
    typeof rawSellingPrice === 'number' && !Number.isNaN(rawSellingPrice)
      ? rawSellingPrice
      : 0;

  const safeBasePrice =
    typeof rawBasePrice === 'number' && !Number.isNaN(rawBasePrice)
      ? rawBasePrice
      : 0;

  const foodCostPercentage =
    safeSellingPrice > 0 ? (safeTotalCost / safeSellingPrice) * 100 : 0;

  const grossProfit = safeSellingPrice - safeTotalCost;

  const getFoodCostStatus = () => {
    if (foodCostPercentage <= 30) return 'good';
    if (foodCostPercentage <= 35) return 'warning';
    return 'danger';
  };

  const foodCostStatus = getFoodCostStatus();

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return '';
    return date.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatNumber = (value, digits = 0) => {
    const n = Number(value);
    if (Number.isNaN(n)) return '0';
    return n.toFixed(digits);
  };

  // ---------- RENDER ----------

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-teal-500 flex items-center justify-center">
              <ChefHat size={20} className="text-white" />
            </div>
            <div>
              <h2 className="font-semibold text-slate-900">{menuItemName}</h2>
              <p className="text-xs text-slate-500">{categoryName}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-600"
          >
            <X size={18} />
          </button>
        </div>

        {/* Cost Summary Bar */}
        <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-6">
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                    Selling
                  </span>
                  <p className="text-lg font-bold text-slate-900">
                    ₹{formatNumber(safeSellingPrice, 0)}
                  </p>
                </div>
                <div className="text-slate-300">−</div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                    Cost
                  </span>
                  <p className="text-lg font-bold text-slate-700">
                    ₹{formatNumber(safeTotalCost, 0)}
                  </p>
                </div>
                <div className="text-slate-300">=</div>
                <div>
                  <span className="text-[10px] text-slate-400 uppercase tracking-wide">
                    Profit
                  </span>
                  <p
                    className={`text-lg font-bold ${
                      grossProfit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}
                  >
                    ₹{formatNumber(grossProfit, 0)}
                  </p>
                </div>
              </div>

              <div className="text-xs text-slate-500">
                Base price:&nbsp;
                <span className="font-medium text-slate-700">
                  ₹{formatNumber(safeBasePrice, 0)}
                </span>
              </div>
            </div>

            {/* Food Cost Badge */}
            <div
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${
                foodCostStatus === 'good'
                  ? 'bg-green-100 text-green-700'
                  : foodCostStatus === 'warning'
                  ? 'bg-amber-100 text-amber-700'
                  : 'bg-red-100 text-red-700'
              }`}
            >
              {foodCostStatus === 'good' ? (
                <TrendingDown size={14} />
              ) : (
                <TrendingUp size={14} />
              )}
              <span className="text-sm font-bold">
                {formatNumber(foodCostPercentage, 1)}%
              </span>
              <span className="text-xs opacity-70">food cost</span>
            </div>
          </div>
        </div>

        {/* Ingredients */}
        <div className="flex-1 overflow-y-auto">
          <div className="px-4 py-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Package size={14} className="text-slate-400" />
                <span className="text-xs font-medium text-slate-500 uppercase tracking-wide">
                  Ingredients ({ingredients.length})
                </span>
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <th className="px-3 py-2 text-left font-medium">Item</th>
                    <th className="px-3 py-2 text-center font-medium w-24">
                      Qty
                    </th>
                    <th className="px-3 py-2 text-right font-medium w-20">
                      Rate
                    </th>
                    <th className="px-3 py-2 text-right font-medium w-20">
                      Cost
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {ingredients.map((ing, idx) => {
                    const qty = Number(ing.quantity ?? 0);
                    const unitCost = Number(ing.unitCost ?? 0);
                    const rowCost =
                      typeof ing.totalCost === 'number'
                        ? ing.totalCost
                        : qty * unitCost;

                    return (
                      <tr key={idx} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-900">
                          {ing.name ?? `Item #${ing.idInventoryItem ?? ''}`}
                        </td>
                        <td className="px-3 py-2 text-center text-slate-600">
                          {qty} {ing.uom ?? ''}
                        </td>
                        <td className="px-3 py-2 text-right text-slate-400 text-xs">
                          ₹{formatNumber(unitCost, 2)}
                        </td>
                        <td className="px-3 py-2 text-right font-semibold text-slate-900">
                          ₹{formatNumber(rowCost, 2)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-teal-50 border-t-2 border-teal-200">
                    <td
                      colSpan={3}
                      className="px-3 py-2 text-right text-sm font-medium text-teal-700"
                    >
                      Total
                    </td>
                    <td className="px-3 py-2 text-right text-base font-bold text-teal-700">
                      ₹{formatNumber(safeTotalCost, 2)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>

          {/* Prep Notes */}
          {prepNotes && (
            <div className="px-4 pb-3">
              <div className="flex items-start gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <FileText
                  size={14}
                  className="text-slate-400 mt-0.5 flex-shrink-0"
                />
                <p className="text-sm text-slate-600">{prepNotes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-slate-200 bg-white flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 px-3 py-1.5 text-slate-500 hover:bg-slate-100 rounded-lg text-sm"
            >
              <Printer size={14} />
              Print
            </button>
            {updatedAt && (
              <span className="text-xs text-slate-400 flex items-center gap-1">
                <Clock size={10} />
                Updated {formatDate(updatedAt)}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-1.5 text-slate-600 hover:bg-slate-100 rounded-lg text-sm"
            >
              Close
            </button>
            {/* Duplicate — only if permitted */}
            {onDuplicate && (
              <button
                onClick={onDuplicate}
                className="flex items-center gap-1.5 px-4 py-1.5 text-blue-600 border border-blue-200 hover:bg-blue-50 rounded-lg text-sm font-medium"
              >
                <Copy size={14} />
                Duplicate
              </button>
            )}
            {/* Edit — only if permitted */}
            {onEdit && (
              <button
                onClick={onEdit}
                className="flex items-center gap-1.5 px-4 py-1.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
              >
                <Edit2 size={14} />
                Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}